import classNames from 'classnames';
import CostCodeCapsules from 'components/domain/cost-code/CostCodeCapsules/CostCodeCapsules';
import EquipmentCapsules from 'components/domain/equipment/EquipmentCapsules/EquipmentCapsules';
import MemberCapsules from 'components/domain/member/MemberCapsules/MemberCapsules';
import ProjectCapsules from 'components/domain/project/ProjectCapsules/ProjectCapsules';
import TimeEntryActionBar from 'components/domain/time-entry/TimeEntryActionBar/TimeEntryActionBar';
import EquipmentTimeEntryDataReport from 'components/domain/time-entry/TimeEntryDataReport/EquipmentTimeEntryDataReport/EquipmentTimeEntryDataReport';
import useEquipmentTimeEntryDataReportData from 'components/domain/time-entry/TimeEntryDataReport/EquipmentTimeEntryDataReport/hooks/useEquipmentTimeEntryDataReportData';
import { ITimeEntryDataTableRow } from 'components/domain/time-entry/TimeEntryDataTable/TimeEntryDataTable';
import ManagedTimeRangeReportDialog from 'components/foundation/dialogs/ManagedTimeRangeReportDialog/ManagedTimeRangeReportDialog';
import { defaultTimeEntryColumns } from 'containers/timesheets/hooks/useTimesheetsColumns';
import { useReduxSelector } from 'hooks';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import { first, isNil, map, partition, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateCheckedTimeEntryIds, updateCheckedTimeOffIds } from 'store/TimeEntryTable/TimeEntryTable';
import { ClassName } from 'types/ClassName';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { dateUtils } from 'utils';
import { onlyHasRemainingItem } from 'utils/collectionUtils';
import { areTimeRangesEqual } from 'utils/dateUtils';
import { t } from 'utils/localize';
import TimeEntryFilterBar from '../../TimeEntryFilterBar/TimeEntryFilterBar';
import './EquipmentTimeEntryDialog.scss';

export interface IEquipmentTimeEntryDialogProps {
  className?: ClassName;
  isOpen: boolean;
  onClose: () => void;
  timeRange: ITimeRange<DateTime>;
  timeRangeType: TimeRangeType;
  equipmentIds?: string[] | null;
  projectIds?: string[] | null;
  memberIds?: string[] | null;
  costCodeIds?: string[] | null;
  includeOpen?: boolean;
  excludeDeleted?: boolean;
  projectIdWithDescendants?: boolean;
  timeRangeBackPressed?: () => boolean; // returns boolean to indicate if the event consumed (true) or if it should perform the default behavior (false)
  timeRangeForwardPressed?: () => boolean; // returns boolean to indicate if the event consumed (true) or if it should perform the default behavior (false)
  onDataChange?: (ids: string[]) => void;
  includeAddOns?: boolean;
}

const EquipmentTimeEntryDialog = ({
  className,
  isOpen,
  onClose,
  timeRange,
  timeRangeType,
  equipmentIds,
  projectIds,
  memberIds,
  costCodeIds,
  includeOpen,
  excludeDeleted,
  projectIdWithDescendants,
  timeRangeBackPressed,
  timeRangeForwardPressed,
  onDataChange,
  includeAddOns,
}: IEquipmentTimeEntryDialogProps) => {
  const filterEquipmentIds = useMemo(() => (equipmentIds ? equipmentIds : undefined), [equipmentIds]);
  const filterCostCodeIds = useMemo(() => (costCodeIds ? costCodeIds : undefined), [costCodeIds]);
  const filterProjectIds = useMemo(() => (projectIds ? projectIds : undefined), [projectIds]);
  const filterMemberIds = useMemo(() => (memberIds ? memberIds : undefined), [memberIds]);

  const dispatch = useDispatch();
  const selectedIds = useReduxSelector((state) =>
    state.timeEntryTable.checkedTimeEntryIds.concat(state.timeEntryTable.checkedTimeOffIds)
  );

  const onSelect = useCallback(
    (rows: ITimeEntryDataTableRow[]) => {
      const [timeEntryIds, timeOffIds] = partition(rows, (row) => isNil(row.timeOffType));
      dispatch(updateCheckedTimeEntryIds(map(timeEntryIds, 'id')));
      dispatch(updateCheckedTimeOffIds(map(timeOffIds, 'id')));
    },
    [dispatch]
  );

  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [filterRange, setFilterRange] = useState<ITimeRange<DateTime>>(timeRange);

  const { data, loading, loadedAll, loadAll, updateEquipmentIds } = useEquipmentTimeEntryDataReportData(
    scroller,
    filterRange,
    includeOpen,
    filterEquipmentIds,
    filterMemberIds,
    filterProjectIds,
    filterCostCodeIds,
    excludeDeleted,
    false,
    projectIdWithDescendants,
    includeAddOns
  );
  const brand = useBrandTitle();

  function renderTimeEntryReport(range: ITimeRange<DateTime>) {
    if (!areTimeRangesEqual(filterRange, range)) {
      setFilterRange(range);
    }
    return (
      <EquipmentTimeEntryDataReport
        columnSettings={defaultTimeEntryColumns}
        timeRange={range}
        selectedIds={selectedIds}
        onSelectChange={onSelect}
        data={data}
        loading={loading}
        updateEquipmentIds={handleUpdate}
        memberIds={memberIds}
        projectId={!onlyHasRemainingItem(projectIds) ? first(projectIds) : undefined}
        costCodeId={!onlyHasRemainingItem(costCodeIds) ? first(costCodeIds) : undefined}
        loadedAll={loadedAll}
        applyPrintOptions={false}
      />
    );
  }

  function handleUpdate(ids: string[]) {
    updateEquipmentIds(ids);
    onDataChange?.(ids);
  }

  function renderActionBar(range: ITimeRange<DateTime>) {
    return (
      <TimeEntryActionBar
        onBulkEditSubmit={(entries) => {
          const ids = uniq(entries.map((entry) => entry.equipment?.id ?? '')).filter((id) => id.length > 0);
          handleUpdate(ids);
        }}
        onDelete={() => {
          handleUpdate(data.map((d) => d.id));
        }}
        onTimeEntryEdit={(data) => {
          const ids = data.members;
          handleUpdate(ids);
        }}
      />
    );
  }
  function renderFilterBar(range: ITimeRange<DateTime>) {
    const exportFileName = `${brand}-${t(
      'equipment-activity-report-time-entries'
    )}-${dateUtils.isoTimeStampLocal()}.csv`;

    return (
      <>
        {(filterMemberIds || filterProjectIds || filterCostCodeIds || filterEquipmentIds) && (
          <TimeEntryFilterBar
            timeRange={range}
            data={data}
            loadedAll={loadedAll}
            loadAll={loadAll}
            exportFileName={exportFileName}
            renderFilters={(forPrint) => (
              <>
                {filterMemberIds && (
                  <MemberCapsules className="ml-2" printVersion={forPrint} memberIds={filterMemberIds} />
                )}
                {filterProjectIds && (
                  <ProjectCapsules
                    className="ml-2"
                    projectIds={filterProjectIds}
                    printVersion={forPrint}
                    isUnassinged={projectIdWithDescendants === false}
                  />
                )}
                {filterCostCodeIds && (
                  <CostCodeCapsules className="ml-2" printVersion={forPrint} costCodeIds={filterCostCodeIds} />
                )}
                {/* We're not printing the equipment capsules because the titles are already on the table headers */}
                {filterEquipmentIds && !forPrint && (
                  <EquipmentCapsules className="ml-2" printVersion={forPrint} equipmentIds={filterEquipmentIds} />
                )}
              </>
            )}
          />
        )}
      </>
    );
  }

  const classes = classNames('equipment-time-entry-dialog', className);

  return (
    <ManagedTimeRangeReportDialog
      className={classes}
      isOpen={isOpen}
      onClose={onClose}
      timeRange={timeRange}
      timeRangeType={timeRangeType}
      reportComponent={renderTimeEntryReport}
      title={t('Time Entries')}
      renderActionBar={renderActionBar}
      renderFilterBar={renderFilterBar}
      setRef={setScroller}
      timeRangeBackPressed={timeRangeBackPressed}
      timeRangeForwardPressed={timeRangeForwardPressed}
      headerClassName={'no-print'}
    />
  );
};

export default EquipmentTimeEntryDialog;
